import { AppstoreAddOutlined, PlusOutlined } from '@ant-design/icons';
import { Table, type TableProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { PageTitle } from '@client/components/PageTitle';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useTable } from '@client/hooks/useTable';
import { useTenants } from '@client/hooks/useTenants';
import { PageLayout } from '@client/layouts/page.layout';
import { columnsMetadata } from '@client/pages/tenants/columns.metadata';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';

import styles from './tenants.module.less';

import type { TColumns } from '@client/types/antd.type';
import type { ITenant } from '@common/types';
import type { ItemType } from 'antd/es/menu/interface';

export const TenantsPage: React.FC = () => {
  const { ability } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useLocale();

  const { loading, entities, total, refetch, onDeactivate, onEdit } =
    useTenants();

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<ITenant>(entities, total);

  const columns: TableProps<ITenant>['columns'] = columnsMetadata({
    filteredInfo,
    sortedInfo,
    locale,
    entities,
    onDelete: onDeactivate,
    onEdit,
  });

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<ITenant>(columns as TColumns<ITenant>[], ['createdAt']);

  const tableProps = {
    columns: filteredColumns as TColumns<ITenant>[],
    pagination: {
      ...pagination,
    },
    scroll: { x: 1200 },
    bordered: true,
    size: 'small' as const,
    className: styles.gridList,
    dataSource: indexable<ITenant>(entities),
    loading,
    rowKey: (record: ITenant) => record.id ?? `row-${Math.random()}`,
    onChange: handleTableChange,
    title: () => (
      <div className={styles.gridHeader}>
        <Toolbar<ITenant[]>
          onRefresh={refetch}
          entities={entities}
          subject={EAbilitySubjects.TENANTS}
          items={
            [
              {
                disabled: ability.cannot(
                  EAbilityActions.CREATE,
                  EAbilitySubjects.TENANT,
                ),
                label: t('actions.add', { type: t('tenant') }),
                icon: <PlusOutlined />,
                onClick: () => navigate('/tenants/new'),
              },
            ] as unknown as ItemType[]
          }
        >
          <HideColumns
            columnsList={columnsList}
            selectedColumns={selectedColumns}
            onChange={setSelectedColumns}
          />
        </Toolbar>
      </div>
    ),
    footer: () => (
      <div className={styles.gridFooter}>
        {t('table.total', { amount: total })}
      </div>
    ),
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.TENANTS}
      title={<PageTitle Icon={AppstoreAddOutlined} name={t('tenants')} />}
    >
      <Table<ITenant> {...tableProps} />
    </PageLayout>
  );
};
